/**
 * CrewPickerModal — role-scoped, single-person staff picker for the
 * CrewSlotEditor (BON-200). Opened by a "+ Add {role}" button; the role is
 * implied by which button opened it, so there is NO position selector here.
 *
 * Searches ACTIVE staff in the venue's region (`useCrewPickerStaff`), excludes
 * anyone already assigned/staged, and on pick fills one slot in that role. A
 * centered modal over a scrim — close via ×, Escape, or scrim click.
 */
import { useEffect, useMemo, useState } from 'react';

import type { CrewPosition } from '../../../../api/workshops';

import { ROLE_CONFIG, memberName } from './crewModel';
import { CrewPickerHeader } from './CrewPickerHeader';
import { CrewPickerResultList } from './CrewPickerResultList';
import { CrewPickerScopeToggle } from './CrewPickerScopeToggle';
import { CrewPickerSearchInput } from './CrewPickerSearchInput';
import { useCrewPickerStaff } from './use-crew-picker-staff';

export interface CrewPickerModalProps {
  position: CrewPosition;
  /** Region to scope the staff search to (the venue's region). */
  regionId: string | null;
  /** userIds already assigned/staged anywhere in the draft — excluded. */
  excludedUserIds: string[];
  /** Pick one person → fills a slot in {@link position}. */
  onPick: (member: { userId: string; name: string }) => void;
  onClose: () => void;
}

export function CrewPickerModal({
  position,
  regionId,
  excludedUserIds,
  onPick,
  onClose,
}: CrewPickerModalProps) {
  const [query, setQuery] = useState('');
  // BON-390: default to the venue's region; the toggle widens to ALL staff
  // (still server-intersected with the caller's own scope) so out-of-region
  // people can be assigned deliberately.
  const [allRegions, setAllRegions] = useState(false);
  const { data: staff, isLoading, error, inRegionIds } = useCrewPickerStaff(regionId, allRegions);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const excluded = useMemo(() => new Set(excludedUserIds), [excludedUserIds]);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (staff ?? [])
      .filter((s) => !excluded.has(s.id))
      .filter((s) => q === '' || memberName(s).toLowerCase().includes(q))
      .slice(0, 50);
  }, [staff, excluded, query]);

  const noun = ROLE_CONFIG[position].noun;

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Add ${noun}`}
        onClick={(e) => e.stopPropagation()}
        className="nb-card"
        style={{
          width: 420,
          maxWidth: '90vw',
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CrewPickerHeader noun={noun} onClose={onClose} />
        <CrewPickerSearchInput value={query} onChange={setQuery} allRegions={allRegions} />
        <CrewPickerScopeToggle allRegions={allRegions} onToggle={() => setAllRegions((v) => !v)} />
        <CrewPickerResultList
          isLoading={isLoading}
          hasError={!!error}
          results={results}
          onPick={onPick}
          inRegionIds={inRegionIds}
        />
      </div>
    </div>
  );
}
